import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverHeader,
	PopoverBody,
	PopoverCloseButton,
	VStack,
	HStack,
	Text,
	Badge,
	Button,
	Box,
	Heading,
	IconButton,
	Alert,
	AlertIcon,
	Spinner,
	Divider,
	useColorModeValue,
} from '@chakra-ui/react';
import {
	FaBell,
	FaCheckDouble,
	FaCheck,
	FaTrash,
	FaEnvelope,
	FaUser,
	FaFileInvoice,
	FaFolder,
	FaList,
	FaTasks,
	FaInfoCircle,
	FaGlobe,
	FaExclamationTriangle,
	FaCheckCircle,
} from 'react-icons/fa';
import { MdNotificationsActive } from 'react-icons/md';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import {
	canNavigateToNotification,
	getNotificationTargetPath,
} from '../../utils/notificationNavigation';
import brandColor from '../../styles/Brand';
import HeaderToolbarIconButton, {
	HeaderToolbarBadge,
} from './HeaderToolbarIconButton';

// Notification icon mapping
const getNotificationIcon = (type) => {
	const iconMap = {
		email: FaEnvelope,
		user: FaUser,
		invoice: FaFileInvoice,
		folder: FaFolder,
		list: FaList,
		task: FaTasks,
		client_portal: FaGlobe,
		info: FaInfoCircle,
		warning: FaExclamationTriangle,
		success: FaCheckCircle,
	};
	return iconMap[type] || FaBell;
};

// Notification color mapping
const getNotificationColor = (type) => {
	const colorMap = {
		email: brandColor.blue,
		user: brandColor.green,
		invoice: brandColor.yellowOrange,
		folder: brandColor.blueIndigo,
		list: brandColor.blueCyan,
		task: brandColor.purple,
		info: brandColor.blue,
		warning: brandColor.yellowOrange,
		success: brandColor.green,
	};
	return colorMap[type] || brandColor.gray;
};

const NotificationSystem = ({ companyId, employeeId, size = 'md' }) => {
	const navigate = useNavigate();
	const {
		// Data
		userNotifications,
		allUserNotifications,
		filteredNotifications,
		loading,
		error,

		// State
		filter,
		isDeletingAll,

		// Setters
		setFilter,

		// Handlers
		handleMarkAsRead,
		handleMarkAsUnread,
		handleDeleteNotification,
		handleMarkAllAsRead,
		handleDeleteAll,
	} = useNotificationManager(companyId, employeeId);

	// Color theming
	const notificationBg = useColorModeValue(
		brandColor.white,
		brandColor.darkGray,
	);

	// Filter notifications for 'read' tab
	let displayNotifications = filteredNotifications;
	if (filter === 'read') {
		displayNotifications = filteredNotifications.filter((notification) => {
			const userRecipient = notification.recipients.find(
				(recipient) => recipient.employeeId === employeeId,
			);
			return userRecipient?.isRead;
		});
	}

	return (
		<Popover placement='bottom-end'>
			{({ onClose: closePopover }) => (
				<>
					<PopoverTrigger>
						<HeaderToolbarIconButton
							aria-label='Notifications'
							icon={<MdNotificationsActive />}
							size={size}
							tone='default'
							badge={
								userNotifications.length > 0 ? (
									<HeaderToolbarBadge>
										{userNotifications.length > 99
											? '99+'
											: userNotifications.length}
									</HeaderToolbarBadge>
								) : null
							}
						/>
					</PopoverTrigger>
					<PopoverContent
						w='420px'
						maxW='95vw'
						bg={notificationBg}
						border='none'
						boxShadow='0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)'
						borderRadius='md'
						overflow='hidden'
						_focus={{
							boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.08)',
						}}>
						<PopoverHeader
							bg={`linear-gradient(135deg, ${brandColor.blueCyan}15 0%, ${brandColor.blue}10 100%)`}
							borderBottomWidth='1px'
							borderColor={`${brandColor.gray}20`}
							p={4}>
							<HStack justify='space-between' align='center'>
								<HStack spacing={3}>
									<Box
										w='8px'
										h='8px'
										bg={brandColor.blueCyan}
										borderRadius='full'
										boxShadow={`0 0 10px ${brandColor.blueCyan}`}
									/>
									<Heading
										size='md'
										color={brandColor.systemDarkBlue}
										fontWeight='600'
										letterSpacing='0.5px'>
										Notifications
									</Heading>
									<Badge colorScheme='teal' variant='subtle' borderRadius='full' px={2}>
										{userNotifications.length} new
									</Badge>
								</HStack>
								<PopoverCloseButton
									size='sm'
									color={brandColor.gray}
									_hover={{ color: brandColor.systemDarkBlue }}
								/>
							</HStack>
						</PopoverHeader>
						<PopoverBody p={0} bg={notificationBg}>
							<VStack
								spacing={0}
								align='stretch'
								maxH='600px'
								overflowY='auto'
								css={{
									'&::-webkit-scrollbar': {
										width: '6px',
									},
									'&::-webkit-scrollbar-track': {
										background: 'transparent',
									},
									'&::-webkit-scrollbar-thumb': {
										background: brandColor.gray + '40',
										borderRadius: '3px',
									},
									'&::-webkit-scrollbar-thumb:hover': {
										background: brandColor.gray + '60',
									},
								}}>
								{/* Filter Buttons */}
								<Box
									p={4}
									bg={`${brandColor.gray}05`}
									borderBottomWidth='1px'
									borderColor={`${brandColor.gray}15`}>
									<HStack spacing={3} justify='center'>
										<Button
											size='sm'
											variant={filter === 'all' ? 'solid' : 'ghost'}
											colorScheme={filter === 'all' ? 'blue' : 'gray'}
											onClick={() => setFilter('all')}
											borderRadius='full'
											px={4}
											fontWeight='500'
											_hover={{
												transform: 'translateY(-1px)',
												boxShadow: 'md',
											}}
											transition='all 0.2s ease'>
											All
											<Badge ml={2} variant='subtle' borderRadius='full'>
												{allUserNotifications.length}
											</Badge>
										</Button>
										<Button
											size='sm'
											variant={filter === 'unread' ? 'solid' : 'ghost'}
											colorScheme={filter === 'unread' ? 'orange' : 'gray'}
											onClick={() => setFilter('unread')}
											borderRadius='full'
											px={4}
											fontWeight='500'
											_hover={{
												transform: 'translateY(-1px)',
												boxShadow: 'md',
											}}
											transition='all 0.2s ease'>
											Unread
											<Badge ml={2} variant='subtle' borderRadius='full'>
												{userNotifications.length}
											</Badge>
										</Button>
										<Button
											size='sm'
											variant={filter === 'read' ? 'solid' : 'ghost'}
											colorScheme={filter === 'read' ? 'green' : 'gray'}
											onClick={() => setFilter('read')}
											borderRadius='full'
											px={4}
											fontWeight='500'
											_hover={{
												transform: 'translateY(-1px)',
												boxShadow: 'md',
											}}
											transition='all 0.2s ease'>
											Read
											<Badge ml={2} variant='subtle' borderRadius='full'>
												{allUserNotifications.length - userNotifications.length}
											</Badge>
										</Button>
									</HStack>
								</Box>
								{/* Action Buttons */}
								<Box
									p={4}
									bg={`${brandColor.gray}03`}
									borderBottomWidth='1px'
									borderColor={`${brandColor.gray}15`}>
									<HStack spacing={3} justify='center'>
										<Button
											size='sm'
											leftIcon={<FaCheckDouble />}
											colorScheme='green'
											variant='outline'
											onClick={handleMarkAllAsRead}
											isDisabled={userNotifications.length === 0}
											borderRadius='full'
											_hover={{
												transform: 'translateY(-1px)',
												boxShadow: 'md',
											}}
											_disabled={{
												opacity: 0.4,
												cursor: 'not-allowed',
											}}
											transition='all 0.2s ease'>
											Mark All Read
										</Button>
										{filter === 'all' && (
											<Button
												size='sm'
												leftIcon={<FaTrash />}
												colorScheme='red'
												variant='outline'
												onClick={handleDeleteAll}
												isLoading={isDeletingAll}
												isDisabled={allUserNotifications.length === 0}
												borderRadius='full'
												_hover={{
													transform: 'translateY(-1px)',
													boxShadow: 'md',
												}}
												_disabled={{
													opacity: 0.4,
													cursor: 'not-allowed',
												}}
												transition='all 0.2s ease'>
												Delete all
											</Button>
										)}
									</HStack>
								</Box>
								{/* Notifications List */}
								{loading ? (
									<Box p={8} textAlign='center'>
										<VStack spacing={4}>
											<Spinner size='lg' color={brandColor.blue} thickness='4px' />
											<Text color={brandColor.gray} fontWeight='500'>
												Loading notifications...
											</Text>
										</VStack>
									</Box>
								) : error ? (
									<Box p={6}>
										<Alert
											status='error'
											borderRadius='lg'
											bg={`${brandColor.red}10`}
											border={`1px solid ${brandColor.red}30`}>
											<AlertIcon color={brandColor.red} />
											<Text color={brandColor.red} fontWeight='500'>
												Error loading notifications
											</Text>
										</Alert>
									</Box>
								) : displayNotifications.length === 0 ? (
									<Box p={8} textAlign='center'>
										<VStack spacing={4}>
											<Box
												w='60px'
												h='60px'
												borderRadius='full'
												bg={`${brandColor.gray}10`}
												display='flex'
												alignItems='center'
												justifyContent='center'>
												<FaBell size='24px' color={brandColor.gray} />
											</Box>
											<VStack spacing={2}>
												<Text
													color={brandColor.systemDarkBlue}
													fontSize='md'
													fontWeight='600'>
													{filter === 'all'
														? 'No notifications yet'
														: `No ${filter} notifications`}
												</Text>
												<Text
													color={brandColor.gray}
													fontSize='sm'
													textAlign='center'
													maxW='250px'>
													{filter === 'unread'
														? "You're all caught up! New notifications will appear here."
														: `All your notifications are ${filter === 'unread' ? 'read' : 'unread'}.`}
												</Text>
											</VStack>
										</VStack>
									</Box>
								) : (
									displayNotifications.map((notification, index) => {
										const userRecipient = notification.recipients.find(
											(recipient) => recipient.employeeId === employeeId,
										);
										const isRead = userRecipient?.isRead;
										const IconComponent = getNotificationIcon(notification.type);
										const iconColor = getNotificationColor(notification.type);
										const targetPath = getNotificationTargetPath(notification);

										return (
											<Box key={notification._id}>
												<Box
													display='flex'
													alignItems='center'
													gap={3}
													p={3}
													bg={isRead ? brandColor.white : `${brandColor.blue}06`}
													boxShadow={isRead ? 'sm' : 'md'}
													borderBottom='1px solid'
													borderColor={isRead ? `${brandColor.gray}20` : `${iconColor}30`}
													position='relative'
													_hover={{
														boxShadow: 'lg',
														bg: isRead ? `${brandColor.gray}06` : `${brandColor.blue}10`,
													}}
													transition='all 0.2s ease'>
													{/* User thumbnail image (if available) */}
													{notification.userAvatarUrl ? (
														<Box
															minW='44px'
															h='44px'
															borderRadius='full'
															overflow='hidden'
															boxShadow='md'
															bg={`${brandColor.gray}10`}
															border={`2px solid ${brandColor.gray}30`}
															display='flex'
															alignItems='center'
															justifyContent='center'>
															<img
																src={notification.userAvatarUrl}
																alt={notification.userName || 'User'}
																style={{
																	width: '100%',
																	height: '100%',
																	objectFit: 'cover',
																}}
															/>
														</Box>
													) : (
														<Box
															minW='44px'
															h='44px'
															borderRadius='full'
															bg={`${iconColor}15`}
															border={`2px solid ${iconColor}25`}
															display='flex'
															alignItems='center'
															justifyContent='center'
															boxShadow={isRead ? 'sm' : 'md'}
															position='relative'>
															<IconComponent color={iconColor} size='22px' />
															{/* Unread indicator */}
															{!isRead && (
																<Box
																	position='absolute'
																	top='-4px'
																	right='-4px'
																	w='12px'
																	h='12px'
																	bg={iconColor}
																	borderRadius='full'
																	border='2px solid white'
																	boxShadow={`0 0 12px ${iconColor}60`}
																	animation='pulse 2s infinite'
																/>
															)}
														</Box>
													)}

													{/* Main Content */}
													<Box flex='1' minW={0}>
														<Text
															fontWeight='700'
															fontSize='md'
															color={brandColor.systemDarkBlue}
															noOfLines={1}
															mb={0}>
															{notification.title}
														</Text>
														<Text fontSize='sm' color={brandColor.gray} mb={0}>
															{notification.message}
														</Text>
														<HStack spacing={2} mt={0}>
															<Text
																fontSize='xs'
																color={brandColor.gray}
																fontWeight='500'
																mb={0}>
																{(() => {
																	const now = new Date();
																	const notifDate = new Date(notification.createdAt);
																	const diffInMinutes = Math.floor(
																		(now - notifDate) / (1000 * 60),
																	);
																	const diffInHours = Math.floor(diffInMinutes / 60);
																	const diffInDays = Math.floor(diffInHours / 24);
																	if (diffInMinutes < 1) return 'Just now';
																	if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
																	if (diffInHours < 24) return `${diffInHours}h ago`;
																	if (diffInDays < 7) return `${diffInDays}d ago`;
																	return notifDate.toLocaleDateString('en-GB', {
																		day: 'numeric',
																		month: 'short',
																	});
																})()}
															</Text>
															{/* Post type badge (e.g. 'New post') */}
															{/* {notification.postType && (
						   <Badge colorScheme='purple' borderRadius='full' fontSize='xs' px={2} py={0.5} fontWeight='bold'>
							 {notification.postType}
						   </Badge>
						 )} */}
															{/* Fallback for legacy 'isNew' */}
															{/* {!notification.postType && (
						   <Badge colorScheme='purple' borderRadius='full' fontSize='xs' px={2} py={0.5} fontWeight='bold'>New post</Badge>
						 )} */}
														</HStack>
													</Box>

													{/* Actions */}
													<VStack align='flex-end' spacing={2} minW='max-content'>
														{canNavigateToNotification(notification) && (
															<Button
																size='xs'
																colorScheme='orange'
																variant='solid'
																borderRadius='full'
																px={3}
																onClick={(e) => {
																	e.stopPropagation();
																	if (!isRead) handleMarkAsRead(notification._id);
																	closePopover();
																	navigate(targetPath);
																}}>
																View
															</Button>
														)}
														<HStack spacing={1}>
															{!isRead ? (
																<IconButton
																	size='xs'
																	variant='ghost'
																	colorScheme='green'
																	icon={<FaCheck />}
																	onClick={(e) => {
																		e.stopPropagation();
																		handleMarkAsRead(notification._id);
																	}}
																	borderRadius='full'
																	w='24px'
																	h='24px'
																	minW='24px'
																	aria-label='Mark as read'
																	_hover={{
																		bg: `${brandColor.green}15`,
																		color: brandColor.green,
																		transform: 'scale(1.05)',
																	}}
																	transition='all 0.2s ease'
																/>
															) : (
																<>
																	<IconButton
																		size='xs'
																		variant='ghost'
																		colorScheme='teal'
																		icon={<FaEnvelope />}
																		onClick={(e) => {
																			e.stopPropagation();
																			handleMarkAsUnread(notification._id);
																		}}
																		borderRadius='full'
																		w='24px'
																		h='24px'
																		minW='24px'
																		aria-label='Mark as unread'
																		_hover={{
																			bg: `${brandColor.blue}15`,
																			color: brandColor.blue,
																			transform: 'scale(1.05)',
																		}}
																		transition='all 0.2s ease'
																	/>
																	<IconButton
																		size='xs'
																		variant='ghost'
																		colorScheme='red'
																		icon={<FaTrash />}
																		onClick={(e) => {
																			e.stopPropagation();
																			handleDeleteNotification(notification._id);
																		}}
																		borderRadius='full'
																		w='24px'
																		h='24px'
																		minW='24px'
																		aria-label='Delete notification'
																		_hover={{
																			bg: `${brandColor.red}15`,
																			color: brandColor.red,
																			transform: 'scale(1.05)',
																		}}
																		transition='all 0.2s ease'
																	/>
																</>
															)}
														</HStack>
													</VStack>
												</Box>
												{index < displayNotifications.length - 1 && (
													<Divider borderColor={`${brandColor.gray}20`} opacity={0.8} />
												)}
											</Box>
										);
									})
								)}
							</VStack>
						</PopoverBody>
					</PopoverContent>
				</>
			)}
		</Popover>
	);
};

export default NotificationSystem;
